import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, RefreshCw, Gift, CreditCard, Sparkles, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import RMAStepper from '@/components/RMAStepper';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { getRMAOffers, getPublicSettings } from '@/lib/api';
import { cn } from '@/lib/utils';

// Helper function to check if item is bridal/wedding dress (respects overrides)
const isBridalItem = (item, overrides = []) => {
  if (overrides && overrides.length > 0) {
    const itemProductId = String(item.product_id || '');
    const override = overrides.find(o => String(o.product_id) === itemProductId);
    if (override) return override.force_category === 'bridal';
  }
  const title = (item.title || '').toLowerCase();
  const productType = (item.product_type || '').toLowerCase();
  const tags = (item.tags || '').toLowerCase();
  const bridalKeywords = ['abito da sposa', 'collezione sposa', 'abiti sposa', 'bride', 'bridal', 'wedding dress'];
  return bridalKeywords.some(keyword => 
    title.includes(keyword) || productType.includes(keyword) || tags.includes(keyword)
  );
};

// Cost constants
const RETURN_COST_BRIDAL_FALLBACK = 100.00;

const OFFER_ICONS = {
  replacement: RefreshCw,
  size_exchange: RefreshCw,
  color_exchange: RefreshCw,
  model_exchange: RefreshCw,
  model_size_exchange: RefreshCw,
  store_credit_bonus: Gift,
  refund: CreditCard
};

const RMAStep2 = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [surveyData, setSurveyData] = useState({});
  const [offers, setOffers] = useState([]);
  const [returnCost, setReturnCost] = useState(0);
  const [eventPassed, setEventPassed] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [eventPassedConfirmed, setEventPassedConfirmed] = useState(false);
  const [rmaBonusPercent, setRmaBonusPercent] = useState(10);
  const [categoryOverrides, setCategoryOverrides] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      const storedOrder = sessionStorage.getItem('currentOrder');
      const storedItems = sessionStorage.getItem('selectedItems');
      const storedSurvey = sessionStorage.getItem('rmaSurveyData');
      
      if (!storedOrder || !storedItems || !storedSurvey) {
        navigate('/');
        return;
      }
      
      const orderData = JSON.parse(storedOrder);
      const items = JSON.parse(storedItems);
      const survey = JSON.parse(storedSurvey);
      
      setOrder(orderData);
      setSelectedItems(items);
      setSurveyData(survey);
      
      // Check if event has passed
      const hasEventPassed = items.some((item, idx) => {
        const itemId = item.unique_id || idx;
        return survey[itemId]?.q2_event_status === 'passed';
      });
      setEventPassed(hasEventPassed);
      
      // Build survey items for API with new field names
      const surveyItems = items.map((item, idx) => {
        const itemId = item.unique_id || idx;
        const data = survey[itemId] || {};
        return {
          product_id: item.id,
          variant_id: item.variant_id,
          title: item.title,
          variant_title: item.variant_title,
          price: item.price,
          image_url: item.image_url,
          q1_event_type: data.q1_event_type || '',
          q2_event_status: data.q2_event_status || '',
          q3_return_reason: data.q3_return_reason || '',
          q3a_defect_type: data.q3a_defect_type || null,
          q4_used_size_guide: data.q4_used_size_guide || null,
          q5_try_on_conditions: data.q5_try_on_conditions || [],
          q6_time_to_event: data.q6_time_to_event || null,
          q7_preferred_solution: data.q7_preferred_solution || '',
          q8_fit_detail: data.q8_fit_detail || []
        };
      });
      
      try {
        const _order = JSON.parse(sessionStorage.getItem('currentOrder') || '{}');
        const _origTotal = _order?.is_exchange_order ? parseFloat(_order.original_order_total || '0') : null;
        const response = await getRMAOffers(surveyItems, _origTotal);
        setOffers(response.offers || []);
        setReturnCost(response.return_cost || 0);
      } catch (error) {
        console.error('Error loading offers:', error);
      }
      
      // Load RMA voucher bonus from admin settings
      try {
        const pubSettings = await getPublicSettings();
        const bonus = pubSettings.rma_voucher_bonus_percent;
        if (bonus !== undefined && bonus !== null) setRmaBonusPercent(Number(bonus));
        if (pubSettings.product_category_overrides) setCategoryOverrides(pubSettings.product_category_overrides);
      } catch (e) { console.warn('[RMAStep2] Settings load failed, using defaults:', e.message); }
      
      setIsLoading(false);
    };
    
    loadData();
  }, [navigate]);

  // Check if any item is bridal
  const hasBridalItem = selectedItems.some(item => isBridalItem(item, categoryOverrides));
  
  // For exchange orders, use original_order_total instead of summing discounted prices
  const currentOrder = JSON.parse(sessionStorage.getItem('currentOrder') || '{}');
  const isExchangeOrder = currentOrder?.is_exchange_order;
  const rawTotalValue = selectedItems.reduce((sum, item) => sum + parseFloat(item.price), 0);
  const totalValue = isExchangeOrder && currentOrder?.original_order_total 
    ? parseFloat(currentOrder.original_order_total) 
    : rawTotalValue;
  const numItems = selectedItems.length;
  
  // Check if any item is Elite (price > €298, non-bridal)
  const ELITE_PRICE_THRESHOLD = 298;
  const hasEliteItem = !hasBridalItem && selectedItems.some(item => parseFloat(item.price) > ELITE_PRICE_THRESHOLD);
  
  // Use return cost from API (backend calculates with dynamic per-item thresholds)
  const calculatedReturnCost = returnCost || (
    hasBridalItem ? RETURN_COST_BRIDAL_FALLBACK : 
    (hasEliteItem ? 45 : 35) + Math.max(0, numItems - 1) * 15
  );
  const refundAmount = totalValue - calculatedReturnCost;

  const handleSelectSolution = (solutionType) => {
    if (eventPassed && !eventPassedConfirmed) {
      toast.error('Conferma prima la dichiarazione sul prodotto non utilizzato');
      return;
    }
    
    setSelectedSolution(solutionType);
  };

  const handleContinue = () => {
    if (!selectedSolution) {
      toast.error('Seleziona una soluzione per continuare');
      return;
    }
    
    if (eventPassed && !eventPassedConfirmed) {
      toast.error('Conferma prima la dichiarazione sul prodotto non utilizzato');
      return;
    }
    
    // Save selection and return cost
    sessionStorage.setItem('rmaChosenOffer', selectedSolution);
    sessionStorage.setItem('rmaReturnCost', String(calculatedReturnCost));
    
    // Route based on selected solution
    if (selectedSolution === 'model_size_exchange') {
      // Redirect to exchange flow
      sessionStorage.setItem('requestType', 'exchange');
      navigate(`/order/${orderNumber}/exchange`);
    } else if (selectedSolution === 'store_credit_bonus') {
      // Redirect to voucher confirmation
      sessionStorage.setItem('requestType', 'voucher');
      navigate(`/order/${orderNumber}/summary`);
    } else {
      // Continue to step 3 for refund or defect replacement
      navigate(`/order/${orderNumber}/rma/step3`);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container bg-white">
        <Header showBack backTo={`/order/${orderNumber}/rma/step1`} />
        <RMAStepper currentStep={2} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Analizziamo le tue risposte...</p>
          </div>
        </main>
      </div>
    );
  }

  // Get primary offer for alternative suggestion
  const primaryOffer = offers.find(o => o.is_primary);
  
  // Disable selection if event passed and not confirmed
  const isDisabled = eventPassed && !eventPassedConfirmed;

  return (
    <div className="page-container bg-white">
      <Header showBack backTo={`/order/${orderNumber}/rma/step1`} />
      <RMAStepper currentStep={2} />
      
      <main className="flex-1 py-8 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-3xl font-normal tracking-tight mb-2">
              Scegli la Soluzione
            </h1>
            <p className="text-muted-foreground font-light mb-8">
              Seleziona come desideri procedere con la tua richiesta
            </p>
          </motion.div>

          {/* Event Passed Warning - Mandatory Checkbox */}
          {eventPassed && (
            <motion.div
              className="bg-amber-50 border border-amber-300 p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-amber-800">
                  Hai indicato che l'evento è già passato. Per procedere, conferma:
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer bg-white p-4 border border-amber-200">
                <Checkbox
                  checked={eventPassedConfirmed}
                  onCheckedChange={setEventPassedConfirmed}
                  className="mt-0.5"
                  data-testid="event-passed-checkbox"
                />
                <span className="text-sm text-amber-800">
                  Confermo che il prodotto <strong>non è stato indossato</strong> all'evento e <strong>non presenta segni d'uso</strong>, 
                  con etichette originali intatte.
                </span>
              </label>
            </motion.div>
          )}

          {/* VOUCHER - Highlighted Option */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <button
              onClick={() => !isDisabled && handleSelectSolution('store_credit_bonus')}
              disabled={isDisabled}
              className={cn(
                "w-full text-left relative overflow-hidden",
                "luxury-card p-0 border-2 transition-all",
                selectedSolution === 'store_credit_bonus'
                  ? "border-emerald-500 ring-4 ring-emerald-200"
                  : "border-emerald-300 hover:border-emerald-400",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
              data-testid="solution-store_credit_bonus"
            >
              {/* Recommended Badge */}
              <div className="absolute top-0 right-0 bg-gradient-to-l from-emerald-600 to-emerald-500 text-white text-xs px-4 py-1.5 font-bold tracking-wide">
                <Sparkles className="w-3 h-3 inline mr-1" />
                CONSIGLIATA
              </div>
              
              {/* Main Content */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Gift className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading text-xl text-emerald-900">Buono Acquisto</h3>
                      {rmaBonusPercent > 0 && (
                        <span className="bg-emerald-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">+{rmaBonusPercent}% BONUS</span>
                      )}
                    </div>
                    <p className="text-sm text-emerald-700 mb-4">
                      {rmaBonusPercent > 0
                        ? `Ricevi un buono da spendere quando vuoi, con un bonus extra del ${rmaBonusPercent}%!`
                        : 'Ricevi un buono da spendere quando vuoi sul nostro sito.'}
                    </p>
                  </div>
                  {selectedSolution === 'store_credit_bonus' && (
                    <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center shadow-md">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Value Calculation */}
              <div className="bg-white p-5 border-t border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Il tuo buono</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-emerald-700">€{(totalValue * (1 + rmaBonusPercent / 100)).toFixed(2)}</span>
                      {rmaBonusPercent > 0 && (
                        <span className="text-sm text-emerald-600 font-medium">
                          (+€{(totalValue * rmaBonusPercent / 100).toFixed(2)} bonus)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Valore articoli: €{totalValue.toFixed(2)}</p>
                    {rmaBonusPercent > 0 && (
                      <p className="text-xs text-emerald-600 font-medium">+ {rmaBonusPercent}% bonus = €{(totalValue * rmaBonusPercent / 100).toFixed(2)}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-100">
                  <div className="flex items-center gap-2 text-xs text-emerald-700">
                    <Check className="w-4 h-4" />
                    <span>Utilizzabile su tutto il catalogo</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-700 mt-1">
                    <Check className="w-4 h-4" />
                    <span>Valido 12 mesi</span>
                  </div>
                </div>
              </div>
            </button>
          </motion.div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Altre opzioni</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Other Options */}
          <div className="space-y-3 mb-8">
            {/* Exchange Option */}
            <motion.button
              onClick={() => !isDisabled && handleSelectSolution('model_size_exchange')}
              disabled={isDisabled}
              className={cn(
                "w-full text-left luxury-card p-5 border transition-all",
                selectedSolution === 'model_size_exchange'
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="solution-model_size_exchange"
            >
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                  selectedSolution === 'model_size_exchange' ? "bg-primary/20" : "bg-secondary"
                )}>
                  <RefreshCw className={cn(
                    "w-5 h-5",
                    selectedSolution === 'model_size_exchange' ? "text-primary" : "text-muted-foreground"
                  )} />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">Cambio Modello/Taglia</h3>
                  <p className="text-sm text-muted-foreground">Scegli un altro modello o una taglia diversa</p>
                </div>
                {selectedSolution === 'model_size_exchange' && (
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </motion.button>

            {/* Refund Option */}
            <motion.button
              onClick={() => !isDisabled && handleSelectSolution('refund')}
              disabled={isDisabled}
              className={cn(
                "w-full text-left luxury-card p-5 border transition-all",
                selectedSolution === 'refund'
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              data-testid="solution-refund"
            >
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                  selectedSolution === 'refund' ? "bg-primary/20" : "bg-secondary"
                )}>
                  <CreditCard className={cn(
                    "w-5 h-5",
                    selectedSolution === 'refund' ? "text-primary" : "text-muted-foreground"
                  )} />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">Rimborso</h3>
                  <p className="text-sm text-muted-foreground">
                    Rimborso di €{refundAmount.toFixed(2)}
                    <span className="text-muted-foreground/70"> (al netto del costo gestione €{calculatedReturnCost.toFixed(2)})</span>
                  </p>
                  {/* Cost breakdown info */}
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    {hasBridalItem 
                      ? "Collezione Sposa: €100 per tessuti pregiati"
                      : hasEliteItem 
                        ? `Elite Collection (>€298): €${calculatedReturnCost.toFixed(0)} per tessuti di pregio` 
                        : `Costo gestione: €${calculatedReturnCost.toFixed(2)}${numItems > 1 ? ` (${numItems} articoli)` : ""}`
                    }
                  </p>
                </div>
                {selectedSolution === 'refund' && (
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </motion.button>
          </div>

          {/* Alternative Offer Suggestion (if available) */}
          {primaryOffer && selectedSolution === 'refund' && (
            <motion.div
              className="bg-emerald-50 border border-emerald-200 p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-800 mb-2">Suggerimento</p>
                  <p className="text-sm text-emerald-700">
                    {primaryOffer.title}: {primaryOffer.description}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            className="flex items-center justify-between mt-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <Button 
              onClick={() => navigate(`/order/${orderNumber}/rma/step1`)}
              variant="outline"
              className="rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Modifica risposte
            </Button>
            
            <Button 
              onClick={handleContinue}
              disabled={!selectedSolution || (eventPassed && !eventPassedConfirmed)}
              className="luxury-btn-primary inline-flex items-center gap-3"
              data-testid="rma-step2-continue"
            >
              Continua
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default RMAStep2;
